import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  FileText,
  FileSearch,
  Search,
  Clock,
  User,
  ArrowRight,
  ExternalLink,
  Filter,
  X,
  Download,
} from "lucide-react";
import { downloadCSV } from "../utils/csv";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_CONFIG = {
  CREATE: { label: "Creación", cls: "bg-[#42E695]/20 text-[#42E695] border-[#42E695]/30" },
  UPDATE: { label: "Actualización", cls: "bg-[#03BEE4]/20 text-[#03BEE4] border-[#03BEE4]/30" },
  DELETE: { label: "Eliminación", cls: "bg-red-500/20 text-red-400 border-red-500/30" },
  AUTH: { label: "Autenticación", cls: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
};

const MODULE_LABELS = {
  customers: "Clientes",
  pipeline: "Pipeline",
  provisions: "Provisión",
  incidents: "Incidencias",
  requests: "Solicitudes",
  it_tickets: "Tickets Internos",
  users: "Usuarios",
  settings: "Configuración",
};

const ALL = "__ALL__";

const AuditLogPage = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ actors: [], modules: [], actions: [] });

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [actorFilter, setActorFilter] = useState(ALL);
  const [moduleFilter, setModuleFilter] = useState(ALL);
  const [actionFilter, setActionFilter] = useState(ALL);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");

  const [selectedLog, setSelectedLog] = useState(null);

  // Debounce the free-text search so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/audit-logs/meta`)
      .then((res) => setMeta(res.data))
      .catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const params = {};
      if (moduleFilter !== ALL) params.module = moduleFilter;
      if (actionFilter !== ALL) params.action = actionFilter;
      if (actorFilter !== ALL) params.actor_id = actorFilter;
      if (dateStart) params.date_start = dateStart;
      if (dateEnd) params.date_end = dateEnd;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      const response = await axios.get(`${API_URL}/api/audit-logs`, { params });
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    } finally {
      setLoading(false);
    }
  }, [moduleFilter, actionFilter, actorFilter, dateStart, dateEnd, debouncedSearch]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const moduleLabel = (m) => MODULE_LABELS[m] || m || "-";

  const actionBadge = (action) => {
    const cfg = ACTION_CONFIG[action] || { label: action, cls: "bg-slate-500/20 text-slate-400 border-slate-500/30" };
    return <Badge className={`${cfg.cls} border`}>{cfg.label}</Badge>;
  };

  const hasActiveFilters =
    moduleFilter !== ALL || actionFilter !== ALL || actorFilter !== ALL || dateStart || dateEnd || searchTerm;

  const clearFilters = () => {
    setSearchTerm("");
    setActorFilter(ALL);
    setModuleFilter(ALL);
    setActionFilter(ALL);
    setDateStart("");
    setDateEnd("");
  };

  const auditDateTime = (v) =>
    v
      ? new Date(v).toLocaleString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  const handleExportCSV = () => {
    if (logs.length === 0) return;
    const columns = [
      { key: "changed_at", label: "Fecha", format: auditDateTime },
      { key: "changed_by_name", label: "Usuario" },
      { key: "module", label: "Módulo", format: (v) => moduleLabel(v) },
      { key: "action", label: "Acción", format: (v) => ACTION_CONFIG[v]?.label || v },
      { key: "entity_type", label: "Tipo entidad" },
      { key: "entity_name", label: "Entidad" },
      { key: "parent_name", label: "Cliente" },
      { key: "field_label", label: "Campo", format: (v, row) => v || row.field_name || "" },
      { key: "old_value", label: "Antes" },
      { key: "new_value", label: "Después" },
      { key: "entity_id", label: "ID entidad" },
    ];
    downloadCSV("auditoria", columns, logs);
  };

  const canNavigate = (log) =>
    log && (log.entity_type === "customer" || (log.parent_id && ["customer_service", "service", "location"].includes(log.entity_type)));

  const handleNavigate = (log) => {
    if (log.entity_type === "customer") {
      navigate(`/customers/${log.entity_id}`);
    } else if (log.parent_id) {
      navigate(`/customers/${log.parent_id}`);
    }
    setSelectedLog(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <FileSearch className="h-8 w-8 text-[#03BEE4]" />
            Registro de Auditoría
          </h1>
          <p className="text-slate-400 mt-1">Historial de acciones y cambios en la plataforma</p>
        </div>
        <Button
          onClick={handleExportCSV}
          disabled={logs.length === 0}
          variant="outline"
          className="border-[#42E695] text-[#42E695] hover:bg-[#42E695]/20 disabled:opacity-40"
          data-testid="export-audit-csv-btn"
        >
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Filter bar */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="relative lg:col-span-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por texto, usuario o ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#0A0A35] border-[#1E1E5F] text-white placeholder:text-slate-500"
                data-testid="audit-search-input"
              />
            </div>

            <Select value={actorFilter} onValueChange={setActorFilter}>
              <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white" data-testid="audit-actor-filter">
                <SelectValue placeholder="Usuario" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-64">
                <SelectItem value={ALL} className="text-white">Todos los usuarios</SelectItem>
                {meta.actors.map((a) => (
                  <SelectItem key={a.id} value={a.id} className="text-white">{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white" data-testid="audit-module-filter">
                <SelectValue placeholder="Módulo" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                <SelectItem value={ALL} className="text-white">Todos los módulos</SelectItem>
                {meta.modules.map((m) => (
                  <SelectItem key={m} value={m} className="text-white">{moduleLabel(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white" data-testid="audit-action-filter">
                <SelectValue placeholder="Acción" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                <SelectItem value={ALL} className="text-white">Todas las acciones</SelectItem>
                {meta.actions.map((a) => (
                  <SelectItem key={a} value={a} className="text-white">{ACTION_CONFIG[a]?.label || a}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                className="bg-[#0A0A35] border-[#1E1E5F] text-white"
                data-testid="audit-date-start"
                aria-label="Fecha inicio"
              />
              <span className="text-slate-500 text-sm">a</span>
              <Input
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                className="bg-[#0A0A35] border-[#1E1E5F] text-white"
                data-testid="audit-date-end"
                aria-label="Fecha fin"
              />
            </div>

            <div className="flex items-center">
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="border-[#1E1E5F] text-slate-300 hover:text-white w-full"
                  data-testid="audit-clear-filters"
                >
                  <X className="h-4 w-4 mr-2" />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardHeader>
          <CardTitle className="text-white font-heading flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#03BEE4]" />
            Historial de Eventos
          </CardTitle>
          <CardDescription className="text-slate-400 flex items-center gap-2">
            <Filter className="h-3.5 w-3.5" />
            {logs.length} eventos encontrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-400">No hay eventos que coincidan con los filtros</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1E1E5F]">
                    <TableHead className="text-slate-400">Fecha</TableHead>
                    <TableHead className="text-slate-400">Usuario</TableHead>
                    <TableHead className="text-slate-400">Módulo</TableHead>
                    <TableHead className="text-slate-400">Acción</TableHead>
                    <TableHead className="text-slate-400">Entidad</TableHead>
                    <TableHead className="text-slate-400">Cambio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow
                      key={log.id}
                      className="border-[#1E1E5F] hover:bg-[#1B1B4B] cursor-pointer"
                      onClick={() => setSelectedLog(log)}
                      data-testid={`audit-row-${log.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2 text-slate-300">
                          <Clock className="h-4 w-4 text-slate-400" />
                          <span className="text-sm whitespace-nowrap">{formatDate(log.changed_at)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-[#1B1B4B] flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-slate-400" />
                          </div>
                          <span className="text-white whitespace-nowrap">{log.changed_by_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-slate-500/20 text-slate-300 border border-slate-500/30">
                          {moduleLabel(log.module)}
                        </Badge>
                      </TableCell>
                      <TableCell>{actionBadge(log.action)}</TableCell>
                      <TableCell>
                        <div className="space-y-0.5 max-w-[220px]">
                          {log.entity_name ? (
                            <span className="text-white font-medium block truncate">{log.entity_name}</span>
                          ) : (
                            <span className="text-slate-500 text-xs font-mono">{log.entity_id?.slice(0, 8)}...</span>
                          )}
                          {log.parent_name && (
                            <span className="text-slate-400 text-xs block truncate">Cliente: {log.parent_name}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm max-w-[240px]">
                          {log.action === "CREATE" ? (
                            <span className="text-[#42E695] truncate">{log.new_value || "Creado"}</span>
                          ) : log.action === "DELETE" ? (
                            <span className="text-red-400 truncate">{log.old_value || log.new_value || "Eliminado"}</span>
                          ) : (
                            <>
                              <span className="text-red-400 line-through truncate">{log.old_value || "(vacío)"}</span>
                              <ArrowRight className="h-3 w-3 text-slate-500 flex-shrink-0" />
                              <span className="text-[#42E695] truncate">{log.new_value || "(vacío)"}</span>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Drawer/Modal */}
      <Dialog open={!!selectedLog} onOpenChange={(o) => !o && setSelectedLog(null)}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-lg" data-testid="audit-detail-modal">
          {selectedLog && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white font-heading flex items-center gap-3">
                  <FileSearch className="h-5 w-5 text-[#03BEE4]" />
                  Detalle del Evento
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div className="flex items-center gap-2">
                  {actionBadge(selectedLog.action)}
                  <Badge className="bg-slate-500/20 text-slate-300 border border-slate-500/30">
                    {moduleLabel(selectedLog.module)}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500 text-xs">Fecha</p>
                    <p className="text-white">{formatDate(selectedLog.changed_at)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Usuario</p>
                    <p className="text-white">{selectedLog.changed_by_name || "-"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Tipo de entidad</p>
                    <p className="text-white">{selectedLog.entity_type}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 text-xs">Entidad</p>
                    <p className="text-white">{selectedLog.entity_name || "-"}</p>
                  </div>
                  {selectedLog.parent_name && (
                    <div className="col-span-2">
                      <p className="text-slate-500 text-xs">Cliente</p>
                      <p className="text-white">{selectedLog.parent_name}</p>
                    </div>
                  )}
                  <div className="col-span-2">
                    <p className="text-slate-500 text-xs">ID de entidad</p>
                    <p className="text-slate-300 font-mono text-xs break-all">{selectedLog.entity_id}</p>
                  </div>
                </div>

                {/* Change / diff */}
                <div className="rounded-lg border border-[#1E1E5F] bg-[#1B1B4B]/40 p-4">
                  <p className="text-slate-400 text-xs mb-2 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    {selectedLog.field_label || selectedLog.field_name || "Cambio registrado"}
                  </p>
                  {selectedLog.action === "CREATE" ? (
                    <p className="text-[#42E695] text-sm">{selectedLog.new_value || "Registro creado"}</p>
                  ) : selectedLog.action === "DELETE" ? (
                    <p className="text-red-400 text-sm">{selectedLog.old_value || selectedLog.new_value || "Registro eliminado"}</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-slate-500 text-xs w-16 flex-shrink-0 pt-0.5">Antes</span>
                        <span className="text-red-400 text-sm break-words">{selectedLog.old_value || "(vacío)"}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-slate-500 text-xs w-16 flex-shrink-0 pt-0.5">Después</span>
                        <span className="text-[#42E695] text-sm break-words">{selectedLog.new_value || "(vacío)"}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-2 border-t border-[#1E1E5F]">
                  {canNavigate(selectedLog) && (
                    <Button
                      variant="outline"
                      onClick={() => handleNavigate(selectedLog)}
                      className="border-[#03BEE4]/40 text-[#03BEE4] hover:bg-[#03BEE4]/10"
                      data-testid="audit-detail-navigate"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Ir a la entidad
                    </Button>
                  )}
                  <Button
                    onClick={() => setSelectedLog(null)}
                    className="bg-[#1B1B4B] text-slate-300 hover:bg-[#252560]"
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogPage;
